import React, { useState, useRef, useEffect } from 'react';
import { Music, Upload, Download, Play, Pause } from 'lucide-react';
import './ToolsCommon.css';

const AudioVisualizer = () => {
  const [audioUrl, setAudioUrl] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [visualizationType, setVisualizationType] = useState('bars');
  const [color, setColor] = useState('#4f46e5');
  const [isRecording, setIsRecording] = useState(false);

  const audioRef = useRef(null);
  const canvasRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyzerRef = useRef(null);
  const animationRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('audio/')) {
      const url = URL.createObjectURL(file);
      setAudioUrl(url);
    }
  };

  const initAudioContext = () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyzerRef.current = audioContextRef.current.createAnalyser();
      analyzerRef.current.fftSize = 256;

      const source = audioContextRef.current.createMediaElementSource(audioRef.current);
      source.connect(analyzerRef.current);
      analyzerRef.current.connect(audioContextRef.current.destination);
    }
  };

  const togglePlayPause = () => {
    if (!audioContextRef.current) {
      initAudioContext();
    }

    if (isPlaying) {
      audioRef.current.pause();
      cancelAnimationFrame(animationRef.current);
    } else {
      audioRef.current.play();
      visualize();
    }
    setIsPlaying(!isPlaying);
  };

  const visualize = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const analyzer = analyzerRef.current;

    const bufferLength = analyzer.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      animationRef.current = requestAnimationFrame(draw);
      analyzer.getByteFrequencyData(dataArray);

      ctx.fillStyle = '#0a0a0a';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      if (visualizationType === 'bars') {
        drawBars(ctx, dataArray, bufferLength);
      } else if (visualizationType === 'wave') {
        drawWave(ctx, dataArray, bufferLength);
      } else if (visualizationType === 'circle') {
        drawCircle(ctx, dataArray, bufferLength);
      }
    };

    draw();
  };

  const drawBars = (ctx, dataArray, bufferLength) => {
    const barWidth = (canvasRef.current.width / bufferLength) * 2.5;
    let x = 0;

    for (let i = 0; i < bufferLength; i++) {
      const barHeight = (dataArray[i] / 255) * canvasRef.current.height;

      const gradient = ctx.createLinearGradient(0, canvasRef.current.height - barHeight, 0, canvasRef.current.height);
      gradient.addColorStop(0, color);
      gradient.addColorStop(1, color + '66');

      ctx.fillStyle = gradient;
      ctx.fillRect(x, canvasRef.current.height - barHeight, barWidth, barHeight);
      x += barWidth + 1;
    }
  };

  const drawWave = (ctx, dataArray, bufferLength) => {
    ctx.lineWidth = 3;
    ctx.strokeStyle = color;
    ctx.beginPath();

    const sliceWidth = canvasRef.current.width / bufferLength;
    let x = 0;

    for (let i = 0; i < bufferLength; i++) {
      const v = dataArray[i] / 255.0;
      const y = v * canvasRef.current.height;

      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }

      x += sliceWidth;
    }

    ctx.stroke();
  };

  const drawCircle = (ctx, dataArray, bufferLength) => {
    const centerX = canvasRef.current.width / 2;
    const centerY = canvasRef.current.height / 2;
    const radius = Math.min(centerX, centerY) - 50;

    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
    ctx.strokeStyle = color + '33';
    ctx.lineWidth = 2;
    ctx.stroke();

    for (let i = 0; i < bufferLength; i++) {
      const angle = (i / bufferLength) * 2 * Math.PI;
      const value = dataArray[i] / 255;
      const barLength = value * 100;

      const x1 = centerX + Math.cos(angle) * radius;
      const y1 = centerY + Math.sin(angle) * radius;
      const x2 = centerX + Math.cos(angle) * (radius + barLength);
      const y2 = centerY + Math.sin(angle) * (radius + barLength);

      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.stroke();
    }
  };

  const startRecording = () => {
    const canvas = canvasRef.current;
    const stream = canvas.captureStream(30);
    const audioStream = audioRef.current.captureStream();

    stream.addTrack(audioStream.getAudioTracks()[0]);

    mediaRecorderRef.current = new MediaRecorder(stream, {
      mimeType: 'video/webm;codecs=vp9'
    });

    chunksRef.current = [];

    mediaRecorderRef.current.ondataavailable = (e) => {
      if (e.data.size > 0) {
        chunksRef.current.push(e.data);
      }
    };

    mediaRecorderRef.current.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: 'video/webm' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = 'audio-visualization.webm';
      link.href = url;
      link.click();
    };

    mediaRecorderRef.current.start();
    setIsRecording(true);
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <div className="tool-container">
      <div className="tool-content-wrapper">
        <div className="tool-header">
          <Music size={48} className="tool-icon" />
          <h1 className="tool-title">Audio Visualizer</h1>
          <p className="tool-subtitle">Create stunning visualizations for your audio</p>
        </div>

        <div className="tool-panel">
          <div className="tool-section">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept="audio/*"
              style={{ display: 'none' }}
            />
            <button
              onClick={() => fileInputRef.current.click()}
              className="tool-button primary"
              style={{ width: '100%' }}
            >
              <Upload size={20} />
              Select Audio File
            </button>
          </div>

          {audioUrl && (
            <>
              <canvas
                ref={canvasRef}
                width={800}
                height={400}
                style={{ width: '100%', borderRadius: '12px', background: '#0a0a0a', marginBottom: '16px' }}
              />

              <audio ref={audioRef} src={audioUrl} onEnded={() => setIsPlaying(false)} style={{ width: '100%', marginBottom: '16px' }} controls />

              <div className="tool-grid">
                <div className="tool-section">
                  <label className="tool-label">Visualization Style</label>
                  <select
                    value={visualizationType}
                    onChange={(e) => setVisualizationType(e.target.value)}
                    className="tool-input"
                    style={{ cursor: 'pointer' }}
                  >
                    <option value="bars">Frequency Bars</option>
                    <option value="wave">Waveform</option>
                    <option value="circle">Circular</option>
                  </select>
                </div>

                <div className="tool-section">
                  <label className="tool-label">Color</label>
                  <div className="color-input-group">
                    <input
                      type="color"
                      value={color}
                      onChange={(e) => setColor(e.target.value)}
                      className="tool-color-input"
                    />
                    <input
                      type="text"
                      value={color}
                      onChange={(e) => setColor(e.target.value)}
                      className="tool-text-input"
                    />
                  </div>
                </div>
              </div>

              <div className="tool-actions">
                <button onClick={togglePlayPause} className="tool-button primary">
                  {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                  {isPlaying ? 'Pause' : 'Play'}
                </button>

                {!isRecording ? (
                  <button
                    onClick={startRecording}
                    disabled={!isPlaying}
                    className="tool-button secondary"
                  >
                    <Download size={20} />
                    Record Visualization
                  </button>
                ) : (
                  <button onClick={stopRecording} className="tool-button secondary">
                    Stop Recording
                  </button>
                )}
              </div>

              <div style={{ marginTop: '16px', padding: '16px', background: '#dbeafe', borderRadius: '12px', fontSize: '14px', color: '#1e40af' }}>
                <strong>Tip:</strong> Play the audio first, then click "Record Visualization" to download the animated visualization as a video file.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AudioVisualizer;
